import {
  Color3,
  Mesh,
  MeshBuilder,
  PBRMaterial,
  TransformNode,
  Vector3,
  type Scene,
} from "@babylonjs/core";
import { hashNoise } from "../../utils/noise";

// Vertical clearance above the ocean plane (which sits at y=-0.05, see
// OceanWater.ts). A near-zero gap (originally 0.05) reliably rendered the
// sand as a flat, unlit grey — WaterMaterial's alpha-blended pass composited
// over it despite the sand geometrically sitting above the water and
// passing the depth test; verified by disabling the ocean mesh entirely
// (sand rendered correctly) and by raising this clearance (fixed it). Not
// investigated further into WaterMaterial internals — a solid ~0.3-unit
// gap is a cheap, reliable fix for this water/beach handoff either way.
const WATERLINE_CLEARANCE = 0.3;

/**
 * Procedural placeholder island geometry (sand + a couple of embedded
 * boulders), art-directed per docs/Castaway Plan.md: warm terracotta/tan
 * rock with hard, faceted (flat-shaded) surfacing rather than smoothed
 * photoreal detail, and smooth, low-noise sand.
 *
 * This is a first pass to unblock Phase 1 rendering/lighting work while
 * Blender (needed for the hero-quality sculpted rock + eventual Blender
 * MCP pipeline) isn't reachable — see docs/Castaway Plan.md status. Swap
 * in a Blender-authored `island.glb` here once available; nothing else in
 * the scene depends on how this mesh was built, only on `IslandTerrain.root`.
 */
export class IslandTerrain {
  readonly root: TransformNode;
  readonly sandMesh: Mesh;
  readonly rockMeshes: Mesh[];
  readonly sandRadius: number;
  /** World Y of the sand surface — other objects (the palm, eventually
   * Johnny) should align to this rather than assuming y=0, since the
   * island sits above the waterline (see WATERLINE_CLEARANCE). */
  readonly groundLevel = WATERLINE_CLEARANCE;

  constructor(scene: Scene, sandRadius = 9) {
    this.sandRadius = sandRadius;
    this.root = new TransformNode("islandRoot", scene);
    this.root.position.y = WATERLINE_CLEARANCE;

    this.sandMesh = this.buildSand(scene, sandRadius);
    this.sandMesh.parent = this.root;

    this.rockMeshes = this.buildRockCluster(scene, sandRadius);
    for (const rock of this.rockMeshes) rock.parent = this.root;
  }

  private buildSand(scene: Scene, radius: number): Mesh {
    const mesh = MeshBuilder.CreateDisc("sand", { radius, tessellation: 64 }, scene);
    // CreateDisc is authored facing +Z; rotate -90° about X so local Z
    // (used below for height) maps to world +Y (up), and the mesh faces up.
    mesh.rotation.x = -Math.PI / 2;

    const positions = mesh.getVerticesData("position");
    if (positions) {
      for (let i = 0; i < positions.length; i += 3) {
        const x = positions[i];
        const y = positions[i + 1];
        const distFromCenter = Math.sqrt(x * x + y * y);
        if (distFromCenter < radius * 0.5) continue; // leave the center vertex untouched
        const angle = Math.atan2(y, x);
        // Gentle irregular coastline rather than a perfect circle.
        const edgeNoise = hashNoise(Math.cos(angle) * 3.1, Math.sin(angle) * 3.1) * 0.08;
        positions[i] = x * (1 + edgeNoise);
        positions[i + 1] = y * (1 + edgeNoise);
      }
      mesh.updateVerticesData("position", positions);
    }
    mesh.createNormals(true);

    const mat = new PBRMaterial("sandMaterial", scene);
    mat.albedoColor = new Color3(0.92, 0.83, 0.64);
    mat.roughness = 0.95;
    mat.metallic = 0;
    // Without a scene environment texture, PBR's Fresnel term still kicks
    // in at grazing view angles (very visible here — the camera looks
    // almost edge-on across this flat ground plane) and mirrors the sky's
    // blue across the whole surface, overpowering the tan albedo entirely.
    // Killing environment-driven specular keeps this a flat, matte,
    // direct-light-only response — matches the art direction's painterly,
    // non-physically-reflective sand anyway.
    mat.environmentIntensity = 0;
    // Safety net against the disc's winding/rotation being flipped — a
    // one-sided ground plane going invisible is a worse failure than a
    // small backface-culling cost on a single low-poly disc. Disabling
    // backFaceCulling alone isn't enough, though: without twoSidedLighting
    // the renderer still lights backfaces with the *front* face's normal,
    // which (if winding is flipped) reads as unlit near-black rather than
    // simply invisible — twoSidedLighting is what actually fixes the light.
    mat.backFaceCulling = false;
    mat.twoSidedLighting = true;
    mesh.material = mat;

    // Never moves — skip recomputing its world matrix every frame (Phase 4 perf pass).
    mesh.freezeWorldMatrix();

    return mesh;
  }

  private buildRockCluster(scene: Scene, sandRadius: number): Mesh[] {
    const mat = new PBRMaterial("rockMaterial", scene);
    mat.albedoColor = new Color3(0.5, 0.33, 0.22); // warm terracotta/tan, per reference
    mat.roughness = 0.85;
    mat.metallic = 0;
    mat.environmentIntensity = 0; // see sandMaterial note — keeps this a flat, matte, direct-light response

    const configs = [
      { position: new Vector3(sandRadius * 0.5, 0, sandRadius * 0.3), scale: new Vector3(1.3, 1.0, 1.1), seed: 11 },
      { position: new Vector3(sandRadius * 0.65, 0, sandRadius * 0.1), scale: new Vector3(0.7, 0.55, 0.75), seed: 47 },
    ];

    return configs.map((cfg, index) => {
      const rock = MeshBuilder.CreateIcoSphere(`rock${index}`, { radius: 1, subdivisions: 2 }, scene);
      const positions = rock.getVerticesData("position");
      if (positions) {
        for (let v = 0; v < positions.length; v += 3) {
          const p = new Vector3(positions[v], positions[v + 1], positions[v + 2]);
          const n = p.length() > 0 ? p.normalizeToNew() : p;
          const noise =
            hashNoise(n.x * 3 + cfg.seed, n.y * 3 - cfg.seed) * 0.5 +
            hashNoise(n.z * 5 + cfg.seed, n.x * 5) * 0.25;
          const displaced = p.scale(1 + noise * 0.35);
          positions[v] = displaced.x;
          positions[v + 1] = displaced.y;
          positions[v + 2] = displaced.z;
        }
        rock.updateVerticesData("position", positions);
      }
      // Hard, faceted normals — the defining trait of the reference's rock
      // rendering (large flat color planes, not smoothed photoreal bump).
      rock.convertToFlatShadedMesh();
      rock.scaling = cfg.scale;
      rock.position = cfg.position.add(new Vector3(0, cfg.scale.y * 0.15, 0)); // partially embedded in sand
      rock.material = mat;
      rock.freezeWorldMatrix(); // never moves (Phase 4 perf pass)
      return rock;
    });
  }

  dispose(): void {
    this.sandMesh.dispose();
    for (const rock of this.rockMeshes) rock.dispose();
    this.root.dispose();
  }
}
